<template>
	<div class="omapi-card-setting-section omapi-analytics-settings">
		<header>
			<h3>Default Analytics</h3>
		</header>
		<div class="omapi-analytics-settings__options">
			<div class="omapi-big-select">
				<common-tooltip
					id="om-analytics-preconfigured"
					tooltip="OM analytics is automatically enabled for all campaigns"
					:icon="false"
					:delay-on-mouse-over="0"
				>
					<label class="omapi-big-select__om-analytics">
						<input type="checkbox" :checked="true" />
						<span class="omapi-big-select-checkmark"></span>
						<img src="../../assets/images/logo-archie.svg" alt="Archie Logo" />

						<b>OM Analytics</b>
						(pre-configured)
					</label>
				</common-tooltip>
				<label id="om-google-analytics" ref="ga">
					<input v-model="showGoogleAnalytics" type="checkbox" />
					<span v-if="isConfigured" class="omapi-big-select-checkmark"></span>
					<img src="../../assets/images/logo-google-analytics.svg" alt="Archie Logo" />
					<b>Google Analytics</b>
				</label>
			</div>
			<div v-if="showGoogleAnalytics" class="omapi-analytics-settings__options-ga">
				<label for="omapi-analytics-settings-id"> Select Your Google Analytics Account: </label>
				<div class="omapi-analytics-settings__options-ga-selects">
					<core-form-fancy-select-input
						id="omapi-analytics-settings-id"
						name="globalAnalyticsID"
						:value="globalAnalyticsID"
						:loading="!analyticsFetched"
						:options="accounts"
						:placeholder="analyticsAccountsPlaceholder"
						search-placeholder="Type to search for an account..."
						@input="sendEvent"
					/>

					<core-form-fancy-select-input
						ref="profiles"
						name="globalAnalyticsProfile"
						:value="globalAnalyticsProfile"
						:loading="!analyticsFetched || fetchingProfiles"
						:options="profiles"
						:placeholder="analyticsProfilesPlaceholder"
						:disabled="!!profilesError"
						search-placeholder="Type to search for a property..."
						:show-no-options="true"
						@input="sendEvent"
					>
						<template #noOptions>{{ analyticsProfilesPlaceholder }}</template>
					</core-form-fancy-select-input>
				</div>
				<div class="omapi-analytics-settings__options-ga-more">
					Or
					<a
						:href="$urls.app('integrations/#add-analytics-connection')"
						class="omapi-link-underline"
						target="_blank"
						>Add a new Google Analytics connection</a
					>
				</div>
			</div>
		</div>
	</div>
</template>

<script lang="js">
import { mapState, mapActions, mapGetters } from 'vuex';
import { scrolltoref } from '@/components/mixins/scrolltoref';

export default {
	mixins: [scrolltoref],
	props: {
		globalAnalyticsID: {
			type: String,
			required: true,
		},
		globalAnalyticsProfile: {
			type: String,
			required: true,
		},
	},
	data() {
		return {
			showGoogleAnalytics: false,
		};
	},
	computed: {
		...mapState('settings', ['analyticsAccounts', 'analyticsProfiles']),
		...mapGetters('settings', ['hasGoogleAnalytics', 'accountProfiles']),
		analyticsFetched() {
			return this.$store.getters.isFetched('siteAnalytics');
		},
		fetchingProfiles() {
			return this.$store.getters.isFetching('profiles-' + this.globalAnalyticsID);
		},
		accounts() {
			return this.analyticsAccounts.map((o) => ({
				name: o.name,
				value: o.id,
			}));
		},
		profilesResult() {
			const { globalAnalyticsID } = this;
			if (!globalAnalyticsID) {
				return [];
			}

			if (!(globalAnalyticsID in this.analyticsProfiles) && this.analyticsFetched) {
				return [];
			}

			return this.accountProfiles(globalAnalyticsID);
		},
		profilesError() {
			const profiles = this.profilesResult;

			return profiles.error || '';
		},
		profiles() {
			const profiles = this.profilesResult;

			if (profiles.error) {
				return [];
			}
			return profiles.map((i) => ({
				name: i.name,
				value: i.id,
			}));
		},
		isConfigured() {
			if (!this.globalAnalyticsID || !this.globalAnalyticsProfile || this.profilesError) {
				return false;
			}

			// Check if profile exists in the given set of profiles.
			return this.profilesResult.find((p) => p.id === this.globalAnalyticsProfile);
		},
		analyticsAccountsPlaceholder() {
			if (!this.analyticsFetched) {
				return 'Loading...';
			}

			return this.accounts.length ? 'Select Your Google Analytics Account' : 'No accounts found...';
		},
		analyticsProfilesPlaceholder() {
			if (!this.globalAnalyticsID) {
				return 'Select an account first...';
			}

			if (this.profilesError) {
				return 'Error with account profiles...';
			}

			if (!this.analyticsFetched || this.fetchingProfiles) {
				return 'Loading...';
			}

			return this.profiles.length ? 'Select a property...' : 'No properties found...';
		},
	},
	watch: {
		hasGoogleAnalytics(newValue) {
			if (0 < newValue) {
				this.showGoogleAnalytics = true;
			}
		},
		showGoogleAnalytics(newVal) {
			this.$emit('toggle', newVal);
		},
		profilesResult(newVal) {
			if (newVal.length) {
				return;
			}

			const id = this.globalAnalyticsID;
			if (!id) {
				return;
			}

			if (!(id in this.analyticsProfiles) && this.analyticsFetched) {
				this.fetchProperties(id).catch(() => {});
			}
		},
	},
	mounted() {
		if (this.hasGoogleAnalytics) {
			this.showGoogleAnalytics = true;
		}
	},
	methods: {
		...mapActions('settings', ['fetchProperties']),
		sendEvent(selected, attrs) {
			this.$emit('input', selected, attrs);
		},
	},
};
</script>
<style lang="scss">
.omapi-analytics-settings {
	&__options-ga {
		> label {
			display: block;
			margin-bottom: 10px;
		}
		&-more {
			display: block;
			margin-top: 10px;
		}
		&-selects {
			display: flex;
			flex-wrap: wrap;
			justify-content: space-between;

			> div {
				flex-basis: calc(50% - 9px);
				width: calc(50% - 9px);
			}
		}
	}
}
</style>
